import {useEffect, useState} from 'react';

/**
 * One 30-second interval shared by every subscriber, instead of a per-component
 * setInterval. Started lazily on first subscriber, stopped once the last one unsubscribes.
 */
const TICK_INTERVAL_MS = 30 * 1000;

const subscribers = new Set();
let intervalId = null;
let tick = 0;

function startTicking() {
    if (intervalId != null) return;
    intervalId = setInterval(() => {
        tick += 1;
        subscribers.forEach((notify) => notify(tick));
    }, TICK_INTERVAL_MS);
}

function stopTicking() {
    if (intervalId == null) return;
    clearInterval(intervalId);
    intervalId = null;
}

/**
 * Subscribes to the shared ticker while `active` is true, returning a counter that
 * increments every 30s to trigger a re-render. Pass `active: false` (e.g. once a
 * timestamp has aged into the static absolute-format window) to unsubscribe — the
 * shared interval itself stops once no component needs it.
 */
export default function useRelativeTimeTick(active = true) {
    const [count, setCount] = useState(tick);

    useEffect(() => {
        if (!active) return undefined;

        subscribers.add(setCount);
        startTicking();

        return () => {
            subscribers.delete(setCount);
            if (subscribers.size === 0) stopTicking();
        };
    }, [active]);

    return count;
}
